/**
 * Single-level repair suggestions for a violating pending edit.
 *
 * When the user's staged change makes a route infeasible (e.g. moving a visit
 * onto a vehicle that is already occupied at that time), the honest local
 * remedies are single follow-up edits on the SAME route — typically unassigning
 * one of its other visits. We simulate the pending edit, then evaluate each
 * candidate follow-up in isolation and keep only those that leave the affected
 * routes fully clean, ranked by cost.
 *
 * Deliberately single-level: candidates are one op deep, never recursive —
 * multi-level cascading belongs to the backend re-solve (with the user's
 * assignments pinned), not to a chain of client guesses the user can't follow.
 * Each suggestion is a normal amendment the user reviews and commits like any
 * other, so the audit log stays truthful.
 */

import { evaluateAmendment } from './amendments';
import { groupRouteStops } from './evaluator';

const DEPOT_TYPES = new Set(['route_start', 'route_end', 'restock']);

/** Merge a pending evaluation's regenerated rows over the current table. */
export function materializeRows(routeStops, newRowsByVehicle) {
  const affected = new Set(newRowsByVehicle.keys());
  const kept = (routeStops || []).filter((r) => !affected.has(String(r.vehicleId)));
  const added = [];
  for (const rows of newRowsByVehicle.values()) added.push(...rows);
  return [...kept, ...added];
}

const entityKey = (row) => (row.tripId != null ? `t|${row.tripId}` : `l|${row.locationId}`);

/**
 * @param data            current data slice ({route_stops} is all that's read)
 * @param pendingAmendment the staged edit
 * @param pendingResult   its evaluateAmendment output (with violations)
 * @param ctx             eval context
 * @returns [{amendment, costDelta}] — candidates whose application (after the
 *          pending edit) leaves the re-evaluated routes violation-free.
 */
export function suggestRepairs(data, pendingAmendment, pendingResult, ctx, { max = 4 } = {}) {
  if (!pendingResult || pendingResult.error || pendingResult.violations.length === 0) return [];

  const dataAfter = { route_stops: materializeRows(data.route_stops, pendingResult.newRowsByVehicle) };
  const byVehicle = groupRouteStops(dataAfter.route_stops);

  // Vehicles that actually carry violations after the pending edit.
  const violatingVehicleIds = new Set(
    pendingResult.violations.map((v) => String(v.entities?.vehicleId)).filter((x) => x && x !== 'undefined')
  );

  // The entity the pending edit itself concerns must not be "repaired" away —
  // suggesting to undo the user's own edit is noise.
  const pendingEntity =
    pendingAmendment.tripId != null ? `t|${pendingAmendment.tripId}` : pendingAmendment.locationId != null ? `l|${pendingAmendment.locationId}` : null;

  const seen = new Set();
  const suggestions = [];
  for (const vehicleId of violatingVehicleIds) {
    const rows = byVehicle.get(vehicleId) || [];
    for (const row of rows) {
      if (DEPOT_TYPES.has(row.stopType)) continue;
      const key = `${vehicleId}|${entityKey(row)}`;
      if (seen.has(key) || entityKey(row) === pendingEntity) continue;
      seen.add(key);

      const candidate = {
        type: 'unassign',
        tripId: row.tripId ?? undefined,
        locationId: row.locationId ?? undefined,
        vehicleId,
        label: `Unassign ${row.locationName ?? row.locationId ?? 'stop'} from vehicle ${vehicleId} to make room`,
      };
      const result = evaluateAmendment(dataAfter, candidate, ctx);
      if (result.error || result.violations.length > 0) continue;
      suggestions.push({ amendment: candidate, costDelta: result.costDelta });
    }
  }

  suggestions.sort((a, b) => a.costDelta.total - b.costDelta.total);
  return suggestions.slice(0, max);
}
